import { History } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { FormField } from '@/components/Shared/FormField';
import type { VehicleLogbookRevisionDto } from '@/store/useVehicleLogbookStore';
import { describeRevision } from '@/pages/VehicleLogbook/logbookText';
import { formatDateTime, parseUtcTimestamp } from '@/utils/dateUtils';

/** The change history of a logbook entry, newest first — the record behind every edit. */
export function LogbookHistory({ revisions }: { revisions: VehicleLogbookRevisionDto[] }) {
  const { t } = useTranslation('vehicles');
  return (
    <section className="border-t border-slate-200 pt-4">
      <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
        <History className="w-4 h-4 text-slate-500" strokeWidth={2} aria-hidden />
        {t('logbook.edit.history')}
      </h3>
      {revisions.length === 0 ? (
        <p className="mt-2 text-[12px] text-slate-500">{t('logbook.edit.noHistory')}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {[...revisions].reverse().map((revision, index) => (
            <li key={index} className="rounded-md border border-slate-200 px-3 py-2 text-[12px]">
              <p className="text-slate-700">{describeRevision(revision, t)}</p>
              <p className="mt-0.5 text-slate-500 tabular-nums">
                {t('logbook.edit.changedBy', {
                  date: formatDateTime(parseUtcTimestamp(revision.changedAt)),
                  name: revision.changedByName,
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface DateTimeFieldsProps {
  dateLabel: string;
  timeLabel: string;
  date: string;
  time: string;
  name: string;
  onChange: (change: { date?: string; time?: string }) => void;
}

/** Date and time of departure or arrival, side by side. */
export function DateTimeFields({ dateLabel, timeLabel, date, time, name, onChange }: DateTimeFieldsProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <FormField
        label={dateLabel}
        name={`${name}Date`}
        type="date"
        value={date}
        onChange={(e) => onChange({ date: e.target.value })}
      />
      <FormField
        label={timeLabel}
        name={`${name}Time`}
        type="time"
        value={time}
        onChange={(e) => onChange({ time: e.target.value })}
        className="tabular-nums"
      />
    </div>
  );
}
